import { BaseDialog } from "@/types/dialog";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { Text } from "../ui/text";
import DownloadIcon from "../ui/icons/DownloadIcon";
import DialogCloseIcon from "../ui/icons/DialogCloseIcon";
import CirclePrevIcon from "../ui/icons/CirclePrevIcon";
import CircleNextIcon from "../ui/icons/CircleNextIcon";
import { useState } from "react";
import QuickStartCloseDialog from "./quick-start-close-dialog";
import { overlay } from "overlay-kit";
import { useTranslation } from "react-i18next";

const QuickStartDialog = ({ isOpen, onClose }: BaseDialog) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isChecked, setIsChecked] = useState(false);

  const handleClose = () => {
    if (!isChecked) {
      onClose();
    } else {
      overlay.open(({ isOpen, close }) => (
        <QuickStartCloseDialog isOpen={isOpen} onClose={() => close()} />
      ));
    }
  };

  const quickStartList = [
    {
      title: t("shared.firstTitle"),
      subTitle: t("shared.firstSubTitle"),
      descriptions: [],
      image: "/images/onboarding_x2_1.webp",
    },
    {
      title: t("shared.secondTitle"),
      subTitle: t("shared.secondSubTitle"),
      descriptions: [
        t("shared.secondDescription1"),
        t("shared.secondDescription2"),
        t("shared.secondDescription3"),
        t("shared.secondDescription4"),
      ],
      image: "/images/onboarding_x2_2.webp",
    },
    {
      title: t("shared.thirdTitle"),
      subTitle: t("shared.thirdSubTitle"),
      descriptions: [
        t("shared.thirdDescription1"),
        t("shared.thirdDescription2"),
        t("shared.thirdDescription3"),
        t("shared.thirdDescription4"),
      ],
      image: "/images/onboarding_x2_3.webp",
    },
    {
      title: t("shared.fourthTitle"),
      subTitle: t("shared.fourthSubTitle"),
      descriptions: [
        t("shared.fourthDescription1"),
        t("shared.fourthDescription2"),
        t("shared.fourthDescription3"),
        t("shared.fourthDescription4"),
      ],
      image: "/images/onboarding_x2_4.webp",
    },
    {
      title: t("shared.fifthTitle"),
      subTitle: t("shared.fifthSubTitle"),
      descriptions: [
        t("shared.fifthDescription1"),
        t("shared.fifthDescription2"),
        t("shared.fifthDescription3"),
      ],
      image: "/images/onboarding_x2_5.webp",
    },
    {
      title: t("shared.sixthTitle"),
      subTitle: t("shared.sixthSubTitle"),
      descriptions: [
        t("shared.sixthDescription1"),
        t("shared.sixthDescription2"),
        t("shared.sixthDescription3"),
        t("shared.sixthDescription4"),
      ],
      image: "/images/onboarding_x2_6.webp",
    },
    {
      title: t("shared.seventhTitle"),
      subTitle: t("shared.seventhSubTitle"),
      descriptions: [
        t("shared.seventhDescription1"),
        t("shared.seventhDescription2"),
        t("shared.seventhDescription3"),
        t("shared.seventhDescription4"),
        t("shared.seventhDescription5"),
      ],
      image: "/images/onboarding_x2_7.webp",
    },
     {
      title: t("shared.eightTitle"),
      subTitle: t("shared.eightSubTitle"),
      descriptions: [
        t("shared.eighthDescription1"),
        t("shared.eighthDescription2"),
        t("shared.eighthDescription3"),
      ],
      image: "/images/onboarding_x2__8.png",
    },
  ];

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = "/[SDF] Quick start.pdf";
    a.download = "[SDF] Quick start.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[1319px] fhd:w-[1627px] h-[698px] fhd:h-[872px] rounded-[10px]">
        {/* Header */}
        <div className="flex justify-between pl-[48px] pr-[16px] h-[88px] border-b border-grey-90">
          <div className="flex gap-[24px] py-[24px] items-center">
            <Text variant={"headline-sm"}>{t("shared.quickStart")}</Text>
            <Button onClick={handleDownload} variant={"secondary"} size={"sm"}>
              <DownloadIcon />
              <Text variant={"title-md"}>PDF</Text>
            </Button>
          </div>
          <div className="flex gap-[24px] items-center">
            <label className="flex items-center gap-[8px] cursor-pointer">
              <input
                type="checkbox"
                className="w-[18px] h-[18px]"
                checked={isChecked}
                onChange={() => setIsChecked((prev) => !prev)}
              />
              <Text variant={"title-sm"} className="text-grey-40">
                {t("jobPlanning.dontShowAgain")}
              </Text>
            </label>
            <button onClick={handleClose}>
              <DialogCloseIcon color="#999999" />
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="flex h-[610px] fhd:h-[784px]">
          <div className="w-[535px] fhd:w-[539px]  overflow-y-auto h-full pl-[48px] pr-[16px] py-[40px] border-r border-grey-90 flex flex-col gap-[48px]">
            {quickStartList.map((item, idx) => (
              <button
                className="flex flex-col gap-2 items-start"
                key={idx}
                onClick={() => setCurrentStep(idx)}
              >
                <Text
                  variant={"title-lg"}
                  className={`${
                    currentStep === idx ? "text-grey-20" : "text-grey-70"
                  }`}
                >
                  {item.title}
                </Text>
                <Text
                  variant={"body-lg"}
                  className={`whitespace-pre-line text-left ${
                    currentStep === idx ? "text-grey-20" : "text-grey-70"
                  }`}
                  dangerouslySetInnerHTML={{ __html: item.subTitle }}
                ></Text>
              </button>
            ))}
          </div>
          <div className="px-[32px] pt-[40px] pb-[32px] flex flex-col gap-4 overflow-y-auto ">
            <div className="h-[405px] fhd:h-[575px] ">
              <img
                src={quickStartList[currentStep].image}
                alt="image"
                className="max-h-[405px] fhd:max-h-[575px] max-w-[720px] fhd:max-w-[1024px] object-contain"
              />
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col w-[466px] fhd:w-[800px]">
                {quickStartList[currentStep]?.descriptions?.map(
                  (description, idx) => (
                    <Text
                      key={idx}
                      variant={"body-lg"}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )
                )}
              </div>
              <div className="flex gap-4 pt-[56px]">
                <button
                  className="h-[40px] w-[40px]"
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                  disabled={currentStep === 0}
                >
                  <CirclePrevIcon color={"#CCCCCC"} />
                </button>
                {currentStep !== quickStartList.length - 1 && (
                  <button
                    className="h-[40px] w-[40px]"
                    onClick={() => setCurrentStep((prev) => prev + 1)}
                    disabled={currentStep === quickStartList.length - 1}
                  >
                    <CircleNextIcon color={"#1DB3A4"} />
                  </button>
                )}
                {currentStep === quickStartList.length - 1 && (
                  <Button
                    variant={"primary"}
                    size={"lg"}
                    className="w-[100px] h-[40px] "
                    onClick={handleClose}
                  >
                    <Text variant={"title-md"} className="text-white">
                      {t("shared.finish")}
                    </Text>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickStartDialog;
